// hooks/useMultilingual.js
// Custom hook for managing multilingual content for products and variants
import { useLanguage } from '../context/LanguageContext';
import {
    getLocalizedText,
    getLocalizedProductName,
    getLocalizedProductDescription,
    getLocalizedOptionName,
    getLocalizedOptionValue,
    createLocalizedDisplayVariantText
} from '../utils/multilingualUtils';

/**
 * Custom hook for multilingual content management
 * @returns {object} Multilingual utility functions
 */
export const useMultilingual = () => {
    const { language } = useLanguage();

    return {
        // Current language
        currentLanguage: language,

        // Text utilities
        getLocalizedText: (textData, fallbackLanguage = 'en') =>
            getLocalizedText(textData, language, fallbackLanguage),

        // Product utilities
        getProductName: (product) =>
            getLocalizedProductName(product, language),

        getProductDescription: (product) =>
            getLocalizedProductDescription(product, language),

        // Option utilities
        getOptionName: (option) =>
            getLocalizedOptionName(option, language),

        getOptionValue: (value, optionName) =>
            getLocalizedOptionValue(value, optionName, language),

        // Variant utilities
        getVariantDisplayText: (product, variant) =>
            createLocalizedDisplayVariantText(product, variant, language),

        // Utility to format product for display
        formatProduct: (product) => {
            if (!product) return null;

            return {
                ...product,
                displayName: getLocalizedProductName(product, language),
                displayDescription: getLocalizedProductDescription(product, language),
                localizedOptions: product.options ? product.options.map(option => ({
                    ...option,
                    displayName: getLocalizedOptionName(option, language),
                    localizedValues: option.values ? option.values.map(value =>
                        getLocalizedOptionValue(value, option.name, language)
                    ) : []
                })) : []
            };
        },

        // Utility to format variant for display
        formatVariant: (product, variant) => {
            if (!variant) return null;

            return {
                ...variant,
                displayText: createLocalizedDisplayVariantText(product, variant, language)
            };
        }
    };
};

export default useMultilingual;
